package com.havn.app.core.billing

import com.havn.app.features.agency.domain.HavnSubscriptionTier

enum class HavnPlanTier {
    Free,
    SoloAgent,
    ProAgent,
    TeamAgent
}

data class HavnPlanLimits(
    val visibleProperties: Int,
    val maxProperties: Int,
    val maxOpeningsPerProperty: Int,
    val maxContacts: Int,
    val maxOpenings: Int,
    val maxLeads: Int,
    val maxPhotosPerProperty: Int,
    val maxDocumentsPerProperty: Int,
    val documentsStorageBytes: Long,
    val maxFileSizeBytes: Long,
    val hasAgencyAgenda: Boolean,
    val aiTextGenerationRequestsPerMonth: Int,
    val hasAnalytics: Boolean,
    val hasSellerReports: Boolean,
    val hasTasks: Boolean,
    val hasEmailSupport: Boolean,
    val hasPriorityEmailSupport: Boolean,
    val hasDailyBackups7Days: Boolean
)

object HavnPlans {
    fun limitsFor(tier: HavnSubscriptionTier): HavnPlanLimits = when (tier) {
        HavnSubscriptionTier.Free -> HavnPlanLimits(
            visibleProperties = 1,
            maxProperties = 1,
            maxOpeningsPerProperty = 10,
            maxContacts = 10,
            maxOpenings = 10,
            maxLeads = 100,
            maxPhotosPerProperty = 1,
            maxDocumentsPerProperty = 0,
            documentsStorageBytes = 0L,
            maxFileSizeBytes = 0L,
            hasAgencyAgenda = false,
            aiTextGenerationRequestsPerMonth = 0,
            hasAnalytics = false,
            hasSellerReports = false,
            hasTasks = false,
            hasEmailSupport = false,
            hasPriorityEmailSupport = false,
            hasDailyBackups7Days = false
        )
        HavnSubscriptionTier.SoloAgent -> HavnPlanLimits(
            visibleProperties = 1_000,
            maxProperties = 1_000,
            maxOpeningsPerProperty = 1_000,
            maxContacts = 1_000,
            maxOpenings = 1_000,
            maxLeads = 10_000,
            maxPhotosPerProperty = 10,
            maxDocumentsPerProperty = 10,
            documentsStorageBytes = 104_857_600L,
            maxFileSizeBytes = 10_485_760L,
            hasAgencyAgenda = false,
            aiTextGenerationRequestsPerMonth = 1_000,
            hasAnalytics = false,
            hasSellerReports = false,
            hasTasks = true,
            hasEmailSupport = true,
            hasPriorityEmailSupport = false,
            hasDailyBackups7Days = false
        )
        HavnSubscriptionTier.ProAgent -> HavnPlanLimits(
            visibleProperties = 10_000,
            maxProperties = 10_000,
            maxOpeningsPerProperty = 10_000,
            maxContacts = 10_000,
            maxOpenings = 10_000,
            maxLeads = 100_000,
            maxPhotosPerProperty = 50,
            maxDocumentsPerProperty = 50,
            documentsStorageBytes = 1_073_741_824L,
            maxFileSizeBytes = 52_428_800L,
            hasAgencyAgenda = true,
            aiTextGenerationRequestsPerMonth = 10_000,
            hasAnalytics = true,
            hasSellerReports = true,
            hasTasks = true,
            hasEmailSupport = true,
            hasPriorityEmailSupport = false,
            hasDailyBackups7Days = false
        )
        HavnSubscriptionTier.TeamAgent -> HavnPlanLimits(
            visibleProperties = 100_000,
            maxProperties = 100_000,
            maxOpeningsPerProperty = 1_000_000,
            maxContacts = 250_000,
            maxOpenings = 1_000_000,
            maxLeads = 10_000_000,
            maxPhotosPerProperty = 100,
            maxDocumentsPerProperty = 100,
            documentsStorageBytes = 10_737_418_240L,
            maxFileSizeBytes = 104_857_600L,
            hasAgencyAgenda = true,
            aiTextGenerationRequestsPerMonth = 50_000,
            hasAnalytics = true,
            hasSellerReports = true,
            hasTasks = true,
            hasEmailSupport = true,
            hasPriorityEmailSupport = true,
            hasDailyBackups7Days = true
        )
    }

    val freeLimits: HavnPlanLimits = limitsFor(HavnSubscriptionTier.Free)
}
